// src/components/SystemAiChatModal.js
// Asistente de IA del SISTEMA (Dashboard): consulta el estado de todos los
// trabajos, sus tareas, análisis de problemas y asignaciones. Llama a la Cloud
// Function chatAiSystem, que se apoya en datos reales de Firestore.
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, BrainCircuit, User } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const SystemAiChatModal = ({ isOpen, onClose, user }) => {
    const functions = getFunctions(getApp(), 'us-central1');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setMessages([{ sender: 'ai', text: `¡Hola ${user?.displayName || user?.email || ''}! Soy el asistente del sistema. Pregúntame por el estado de los trabajos, tareas pendientes, problemas o asignaciones.` }]);
        }
    }, [isOpen, user]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || isLoading) return;

        const history = messages.map(m => ({ role: m.sender === 'user' ? 'user' : 'model', content: m.text }));

        setMessages(prev => [...prev, { sender: 'user', text }]);
        setInput('');
        setIsLoading(true);
        try {
            const chatAiSystem = httpsCallable(functions, 'chatAiSystem');
            const result = await chatAiSystem({ userMessage: text, history });
            setMessages(prev => [...prev, { sender: 'ai', text: result.data.response || 'No pude procesar tu pregunta.' }]);
        } catch (error) {
            console.error('Error chatAiSystem:', error);
            setMessages(prev => [...prev, { sender: 'ai', text: `Error de conexión: ${error.message}` }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[70vh] flex flex-col">
                <header className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <BrainCircuit size={20} className="text-purple-600" />
                        Asistente del Sistema
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </header>
                <main className="flex-grow p-4 overflow-y-auto bg-gray-100">
                    <div className="space-y-4">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                                {msg.sender === 'ai' && <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white flex-shrink-0"><BrainCircuit size={18} /></div>}
                                <div className={`p-3 rounded-lg max-w-lg ${msg.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-white text-gray-800'}`}>
                                    <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                                </div>
                                {msg.sender === 'user' && <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0"><User size={18} /></div>}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white flex-shrink-0"><BrainCircuit size={18} /></div>
                                <div className="p-3 rounded-lg bg-white text-gray-800"><p className="text-sm italic">Analizando los trabajos...</p></div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>
                </main>
                <footer className="p-4 border-t bg-white rounded-b-lg">
                    <form onSubmit={handleSend} className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ej: ¿Qué trabajos tienen alertas y a quién están asignadas?"
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            disabled={isLoading}
                        />
                        <button type="submit" className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400" disabled={isLoading || !input.trim()}>
                            <Send size={20} />
                        </button>
                    </form>
                </footer>
            </div>
        </div>
    );
};

export default SystemAiChatModal;
